<template>
  <article class="bonus-card" :class="classes" data-t="bonus-card">
    <div class="wrapper">
      <StTooltipButton
        v-if="tooltipName"
        :data-t-tooltip-name="tooltipName"
        class="tooltip"
        @click="showTooltip"
      />
      <header class="header">
        <div v-if="isVip" class="vip-badge">
          <span>VIP</span>
        </div>

        <div class="image-container" :class="{ paused }">
          <slot name="image">
            <StIcon
              v-if="paused"
              class="pause-icon"
              size="16"
              name="pause-bold-solid"
            />
            <img
              v-if="settingsByType.image"
              class="header-image"
              :src="settingsByType.image"
              alt=""
              width="44"
              height="44"
            />
          </slot>
        </div>

        <div class="header-text">
          <h4
            class="title"
            data-t="title"
            :data-t-level-bonus="`deposit-level-${level}-i49p`"
          >
            <slot name="title">{{ settingsByType.title }}</slot>
            <slot name="title-append" />
          </h4>
          <div class="subtitle" data-t="subtitle">
            <slot name="subtitle"></slot>
          </div>
        </div>
      </header>
      <div v-if="isContentVisible" class="content">
        <div v-if="isStepProgressVisible" class="steps-progress-wrapper">
          <StepsProgress :steps-progress="stepsProgress" />
          <div class="progress-text">
            <slot name="progress-text" />
          </div>
        </div>

        <div v-else-if="isProgressVisible" class="progress" data-t="progress">
          <div class="progress-bar" data-t="progress-bar-qe4f" />
          <div class="progress-text">
            <slot name="progress-text" />
          </div>
        </div>
        <slot />
      </div>
      <div class="actions" :class="{ 'with-timer': timer }" data-t="actions">
        <slot name="timer">
          <StTimer
            v-if="timer"
            :expires-at="timer.expiresAt"
            :label="timer.title"
            class="timer"
            with-days
            :max-days="30"
          />
        </slot>
        <slot name="actions" />
      </div>
    </div>
    <div v-if="highlight" class="highlight" />
  </article>
</template>

<script setup lang="ts">
import StTooltipButton from '@st/ui/components/StTooltip/StTooltipButton.vue'
import { useGetTitleByLevel } from '../../composables/useGetTitleByLevel'
import { useModalBonusTooltip } from '../../composables/useBonusTooltip'
import StepsProgress from '../StepsProgress/StepsProgress.vue'
import depositBonusImg from '../../assets/deposit.webp'
import freespinImg from '../../assets/freespins.webp'
import freebetImg from '../../assets/freebet.webp'
import cashbackImg from '../../assets/cashback.webp'
import rakebackImg from '../../assets/rakeback.webp'
import reloadImg from '../../assets/reload.webp'
import tournamentImg from '../../assets/tournament.webp'
import type { StepRolling } from '../../types'

interface Props {
  id: number | string
  type:
    | 'deposit'
    | 'cash'
    | 'freespin'
    | 'freebet'
    | 'cashback'
    | 'rakeBack'
    | 'reload'
    | 'ratRace'
    | 'vip'
  level?: number
  paused?: boolean
  progress?: string
  stepsProgress?: {
    steps: StepRolling[]
    inProgressStepProgress: string
  }
  timer?: {
    expiresAt: string
    title?: string
  } | null
  highlight?: boolean
}
const {
  id,
  type,
  paused,
  progress,
  level,
  timer,
  highlight = false,
  stepsProgress = {
    steps: [],
    inProgressStepProgress: '0',
  },
} = defineProps<Props>()
const { t } = useI18n()

const slots = useSlots()

const classes = computed(() => [type, { paused }])
const { getTitle } = useGetTitleByLevel()
const settingsByType = computed(() => {
  switch (type) {
    case 'deposit':
    case 'cash':
      return {
        title: level
          ? getTitle(t('bonuses.bonus'), level)
          : t('bonuses.depositTitle'),
        image: depositBonusImg,
      }
    case 'freespin':
      return {
        title: getTitle(t('bonuses.freespins'), level),
        image: freespinImg,
      }
    case 'freebet':
      return {
        title: getTitle(t('bonuses.freebet'), level),
        image: freebetImg,
      }
    case 'cashback':
      return {
        title: t('bonuses.weeklyCashback'),
        image: cashbackImg,
      }
    case 'rakeBack':
      return {
        title: t('bonuses.rakeback'),
        image: rakebackImg,
      }
    case 'reload':
      return {
        title: getTitle(t('bonuses.reload'), level),
        image: reloadImg,
      }
    case 'ratRace':
      return {
        title: t('bonuses.ratRace'),
        image: tournamentImg,
      }
    case 'vip':
      return {
        title: 'VIP',
        image: null,
      }
    default:
      return {
        title: '',
        image: null,
      }
  }
})

const isProgressVisible = computed(() => progress && progress !== '100')
const isStepProgressVisible = computed(() => stepsProgress.steps.length > 1)
const progressPercents = computed(() => `${progress}%`)

const isContentVisible = computed(
  () => slots.default ?? isProgressVisible.value,
)

const { showTooltip, tooltipName } = useModalBonusTooltip(
  computed(() => ({ id, type })),
)

const isVip = computed(() => ['vip', 'cashback', 'reload'].includes(type))
</script>

<style scoped>
.header {
  display: flex;
  gap: var(--spacing-150);
}

.header-text {
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  gap: var(--spacing-025);

  min-width: 0;
  padding-top: var(--spacing-025);
}

.title {
  overflow: hidden;

  margin: 0;
  margin-right: var(--spacing-200);

  font: var(--mobile-text-semibold);
  color: var(--text-primary, #ffffff);
  text-overflow: ellipsis;
  white-space: nowrap;
}

.subtitle {
  overflow: hidden;

  font: var(--mobile-title-2-semibold);
  color: var(--text-warning);
  text-overflow: ellipsis;
  text-shadow:
    0 24px 48px rgb(0 0 0 / 24%),
    0 8px 12px rgb(0 0 0 / 24%);
  white-space: nowrap;
}

.actions {
  display: flex;
  align-items: center;
  justify-content: space-between;

  margin-top: auto;

  border-radius: var(--border-radius-100);

  &.with-timer {
    min-height: 40px;
    background: rgba(255 255 255 / 10%);
  }
}

.timer {
  flex: 1 1 50%;
  min-width: 0;
}

.image-container {
  position: relative;

  display: flex;
  align-items: center;
  justify-content: center;

  width: 44px;
  height: 44px;

  &.paused {
    .header-image {
      opacity: var(--disable-layer-opasity, 0.4);
      filter: blur(1px);
    }
  }
}

.pause-icon {
  position: absolute;
  z-index: 1;
}

.progress {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-050);
  padding: var(--spacing-050) 0;
}

.progress-bar {
  position: relative;

  height: 6px;

  background: var(--background-ghost-tertiary);
  background-size: contain;
  border-radius: var(--border-radius-100);

  &::after {
    content: '';

    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;

    width: v-bind(progressPercents);

    background-color: var(--background-success);
    border-radius: var(--border-radius-100);
  }
}

.steps-progress-wrapper {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-050);
  padding: var(--spacing-050) 0;
}

.progress-text {
  font: var(--mobile-caption-1-regular);
}

.bonus-card {
  position: relative;

  overflow: hidden;
  display: flex;
  flex-direction: column;

  min-width: 0;
  min-height: 118px;
  padding: var(--spacing-025);

  border-radius: var(--border-radius-150);

  .wrapper {
    display: flex;
    flex-direction: column;
    flex-grow: 1;
    gap: var(--spacing-100);

    padding: var(--spacing-125) var(--spacing-150) var(--spacing-150);

    background: var(--background-primary);
    border-radius: var(--border-radius-150);
  }

  &.deposit,
  &.cash {
    .wrapper {
      background: linear-gradient(95deg, #721528 0%, #3d0b15 100%);
    }
  }

  &.freespin,
  &.freebet {
    .wrapper {
      background: linear-gradient(95deg, #075528 0%, #053319 100%);
    }
  }

  /* stylelint-disable */
  &.rakeBack {
    .wrapper {
      background: linear-gradient(95deg, #1b2b7d 0%, #111c50 100%);
    }
  }

  &.ratRace {
    .wrapper {
      background: linear-gradient(95deg, #792d01 0%, #471c01 100%);
    }
  }
  /* stylelint-enable */
  &.cashback,
  &.reload {
    .wrapper {
      background: linear-gradient(95deg, #8f6600 0%, #523a00 100%),
        linear-gradient(95deg, #1b2b7d 0%, #111c50 100%);
    }
  }

  &.paused {
    .wrapper {
      background: var(--background-primary);
    }

    .subtitle {
      opacity: var(--disable-layer-opasity, 0.4);
    }

    .content {
      opacity: var(--disable-layer-opasity, 0.4);
    }
  }

  .highlight {
    pointer-events: none;

    position: absolute;
    z-index: -1;
    inset: -2px;
    transform: translate(-100%, -100%);

    display: block;

    width: 110%;
    height: 110%;

    background-color: var(--palette-brand-600);

    animation: highlight 1.4s 1s ease-in-out 1 forwards;
  }
}

.tooltip {
  position: absolute;
  top: var(--spacing-125);
  right: var(--spacing-125);
}

.vip-badge {
  position: absolute;
  z-index: 1;
  top: var(--spacing-025);
  left: var(--spacing-025);

  display: flex;
  align-items: center;
  justify-content: center;

  min-width: 20px;
  height: 20px;
  padding: var(--spacing-025) var(--spacing-100);

  font: var(--desktop-text-xs-medium);
  color: var(--text-warning);

  border-radius: var(--border-radius-150) 0 var(--border-radius-075) 0;
  /* stylelint-disable */
  background: #533b01;

  box-shadow:
    0px 8px 16px -4px rgba(0, 0, 0, 0.16),
    0px 4px 8px -4px rgba(0, 0, 0, 0.24);
  /* stylelint-enable */
}

@keyframes highlight {
  0% {
    transform: translate(-90%, -80%);
  }

  14% {
    transform: translate(-70%, -30%);
  }

  28% {
    transform: translate(-5%, -5%);
  }

  70% {
    transform: translate(-5%, -5%);
  }

  100% {
    transform: translate(100%, 100%);
  }
}
</style>
